import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { apiFetch } from '../lib/apiClient';
import { 
  TrendingUp, 
  Award, 
  Target, 
  Calendar,
  CheckCircle2,
  Star,
  Flame,
  Clock,
  BookOpen,
  Palette,
  Eye,
  Layers,
  Sparkles,
  Trophy,
  Medal,
  Zap
} from 'lucide-react';

export default function ProgressPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      try {
        const response = await apiFetch('/progress/dashboard', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const message = data?.message || data?.error || 'Unable to load progress.';
          throw new Error(message);
        }

        if (isMounted) {
          setDashboard(data.dashboard || data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load progress.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadStreak = async () => {
      try {
        const response = await apiFetch('/streak/current', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (response.ok && isMounted) {
          setStreak(data.streak || data);
        }
      } catch {
        // Streak section will use fallbacks
      }
    };

    loadStreak();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json().catch(() => ({}));
        if (response.ok && isMounted) {
          setProfile(data.profile || data);
        }
      } catch {
        // Timeline will show empty fallback
      } finally {
        if (isMounted) setProfileLoading(false);
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  const currentSkills = dashboard?.currentSkills || {};
  const statistics = dashboard?.statistics || {};
  const apiMilestones = dashboard?.milestones || [];

  const streakCurrent = streak?.current ?? 0;
  const streakLongest = streak?.longest ?? 0;
  const nextMilestone = streak?.nextMilestone;
  const threshold = nextMilestone?.threshold ?? 7;
  const daysRemaining = nextMilestone?.daysRemaining ?? Math.max(0, threshold - streakCurrent);

  const weeklyProgressPercent = threshold > 0 ? Math.min(100, Math.round((streakCurrent / threshold) * 100)) : 0;
  const overallProgressPercent = streakCurrent >= threshold ? 100 : weeklyProgressPercent;

  const skillIconMap = {
    perspective: Layers,
    anatomy: Eye,
    shading: Sparkles,
    composition: Target,
    lineQuality: Sparkles,
    colorTheory: Palette,
  };

  const skillMetrics = Object.entries(currentSkills).map(([key, value]) => {
    const icon = skillIconMap[key] || TrendingUp;
    const labelMap = {
      perspective: 'Perspective',
      anatomy: 'Anatomy & Figure',
      shading: 'Shading',
      composition: 'Composition',
      lineQuality: 'Line Quality',
      colorTheory: 'Color Theory',
    };
    return {
      skill: labelMap[key] || key,
      level: Number(value || 0),
      maxLevel: 10,
      change: '',
      icon,
    };
  });

  const achievements = apiMilestones.map((m, idx) => ({
    id: m.milestoneId || `m-${idx}`,
    title: m.description || m.type || 'Milestone',
    description: m.description || '',
    earned: true,
    date: m.achievedAt?.split('T')[0] || '',
    icon: Award,
    rarity: 'common',
  }));

  const skillLabelMap = {
    perspective: 'Perspective',
    anatomy: 'Anatomy',
    shading: 'Shading',
    composition: 'Composition',
    lineQuality: 'Line Quality',
    colorTheory: 'Color Theory',
  };

  const timelineItems = (() => {
    const roadmap = profile?.currentRoadmap;
    const available = roadmap?.availableExercises || [];
    const completed = roadmap?.completedExercises || [];

    if (!available.length && !completed.length) return [];

    const completedIds = new Set(completed.map(c => c.exerciseId));
    const allExercises = [...completed, ...available];

    let foundCurrent = false;
    return allExercises.map((ex) => {
      const isCompleted = completedIds.has(ex.exerciseId);
      const isCurrent = !isCompleted && !foundCurrent;
      if (isCurrent) foundCurrent = true;

      const targetSkills = ex.targetSkills || [];
      const skillDetails = targetSkills.map((sk) => {
        const level = Number(currentSkills[sk] ?? 0);
        return {
          key: sk,
          label: skillLabelMap[sk] || sk,
          level,
          maxLevel: 10,
          progress: Math.round((level / 10) * 100),
        };
      });
      const avgProgress = skillDetails.length
        ? Math.round(skillDetails.reduce((s, d) => s + d.progress, 0) / skillDetails.length)
        : 0;

      return {
        id: ex.exerciseId,
        title: ex.title,
        status: isCompleted ? 'completed' : isCurrent ? 'current' : 'upcoming',
        targetSkills: skillDetails,
        progress: avgProgress,
        duration: ex.duration ? `${ex.duration} min` : null,
      };
    });
  })();

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'from-slate-400 to-slate-500';
      case 'rare': return 'from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)]';
      case 'epic': return 'from-[hsl(262,83%,58%)] to-pink-500';
      case 'legendary': return 'from-[hsl(12,95%,65%)] to-orange-500';
      default: return 'from-slate-400 to-slate-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,36%)]" />;
      case 'current': return <div className="w-4 h-4 rounded-full bg-[hsl(234,89%,63%)] animate-pulse" />;
      default: return <div className="w-4 h-4 rounded-full border-2 border-slate-300" />;
    }
  };

  const overviewStats = [
    {
      label: 'Current Streak',
      value: `${statistics.currentStreak || 0} days`,
      icon: Flame,
      color: 'hsl(12,95%,65%)',
    },
    {
      label: 'Exercises Done',
      value: statistics.totalExercisesCompleted || 0,
      icon: BookOpen,
      color: 'hsl(262,83%,58%)',
    },
    {
      label: 'Milestones',
      value: statistics.milestonesAchieved || 0,
      icon: Award,
      color: 'hsl(234,89%,63%)',
    },
    {
      label: 'Longest Streak',
      value: `${statistics.longestStreak || 0} days`,
      icon: Trophy,
      color: 'hsl(234,89%,63%)',
    },
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="progress-page">
      <DashboardSidebar />
      
      <main className="lg:ml-60 p-4 pt-24 lg:p-8">
        {/* Header */}
        <div className="mb-8" data-testid="progress-header">
          <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">Progress Dashboard</h1>
          <p className="text-slate-600 mt-0.5">Track your skill metrics and achievements</p>
          {error && !loading && (
            <p className="text-xs text-red-500 mt-1" data-testid="progress-error">
              {error}
            </p>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200/60 p-1 rounded-full flex w-full justify-start overflow-x-auto h-full hide-scroll sm:w-auto sm:justify-center" data-testid="progress-tabs">
            <TabsTrigger 
              value="overview" 
              className="rounded-full px-5 text-sm whitespace-nowrap data-[state=active]:bg-[hsl(234,89%,63%)] data-[state=active]:text-white"
              data-testid="tab-overview"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="skills" 
              className="rounded-full px-5 text-sm whitespace-nowrap data-[state=active]:bg-[hsl(234,89%,63%)] data-[state=active]:text-white"
              data-testid="tab-skills"
            >
              Skill Metrics
            </TabsTrigger>
            <TabsTrigger 
              value="achievements" 
              className="rounded-full px-5 text-sm whitespace-nowrap data-[state=active]:bg-[hsl(234,89%,63%)] data-[state=active]:text-white"
              data-testid="tab-achievements"
            >
              Achievements
            </TabsTrigger>
            <TabsTrigger 
              value="timeline" 
              className="rounded-full px-5 text-sm whitespace-nowrap data-[state=active]:bg-[hsl(234,89%,63%)] data-[state=active]:text-white"
              data-testid="tab-timeline"
            >
              Timeline
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {loading ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-24 bg-white rounded-xl border border-slate-100 shadow-sm animate-pulse p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-3 mt-1">
                          <div className="h-3 w-20 bg-slate-200/60 rounded"></div>
                          <div className="h-6 w-24 bg-slate-200/60 rounded"></div>
                        </div>
                        <div className="w-10 h-10 bg-slate-200/60 rounded-xl"></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="h-64 bg-white rounded-xl border border-slate-100 shadow-sm animate-pulse"></div>
                  <div className="h-64 bg-white rounded-xl border border-slate-100 shadow-sm animate-pulse"></div>
                </div>
              </div>
            ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-stats">
              {overviewStats.map((stat, idx) => (
                <Card key={idx} className="card-elevated border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">{stat.label}</p>
                        <p className="text-xl font-bold text-[hsl(222,47%,11%)]">{stat.value}</p>
                      </div>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color.replace(')', ' / 0.1)')}` }}>
                        <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* This Week & Overall Progress */}
            <div className="grid grid-cols-2 gap-6">
              {/* This Week */}
              <Card className="card-elevated border-0" data-testid="weekly-activity">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                    This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">
                        {streakCurrent}
                        <span className="text-xl font-normal text-slate-400">/{threshold}</span>
                      </p>
                      <p className="text-sm text-slate-600 mt-1">days practiced</p>
                    </div>
                  </div>
                  <Progress value={weeklyProgressPercent} className="h-2 mb-4" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      {streakCurrent >= threshold
                        ? `${threshold}-day streak reached!`
                        : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} to ${threshold}-day milestone`}
                    </span>
                    <span className="text-[hsl(234,89%,63%)] font-medium">
                      {weeklyProgressPercent}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Overall Progress */}
              <Card className="card-elevated border-0" data-testid="overall-progress">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                    Streak Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                      <svg className="w-28 h-28 transform -rotate-90">
                        <circle
                          cx="56"
                          cy="56"
                          r="48"
                          stroke="hsl(220 13% 91%)"
                          strokeWidth="10"
                          fill="none"
                        />
                        <circle
                          cx="56"
                          cy="56"
                          r="48"
                          stroke="url(#streakProgressGradient)"
                          strokeWidth="10"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${overallProgressPercent * 3.02} 302`}
                        />
                        <defs>
                          <linearGradient id="streakProgressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="hsl(234 89% 63%)" />
                            <stop offset="100%" stopColor="hsl(262 83% 58%)" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-[hsl(222,47%,11%)]">{overallProgressPercent}%</span>
                        <span className="text-xs text-slate-500">to {threshold}-day</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <Badge className="bg-[hsl(262,83%,58%)]/10 text-[hsl(262,83%,58%)]">
                      {streakLongest} day longest streak
                    </Badge>
                    <p className="text-xs text-slate-600 mt-2">
                      {streakCurrent >= threshold
                        ? 'Next milestone: keep it going!'
                        : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining to ${threshold}-day streak`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Skills Overview */}
            <Card className="card-elevated border-0" data-testid="quick-skills">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Outfit']">Skill Metrics Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {skillMetrics.length > 0 ? (
                    skillMetrics.map((skill, idx) => (
                      <div key={idx} className="text-center">
                        <div className="w-14 h-14 rounded-full bg-[hsl(234,89%,63%)]/10 flex items-center justify-center mx-auto mb-2">
                          <skill.icon className="w-6 h-6 text-[hsl(234,89%,63%)]" />
                        </div>
                        <p className="text-lg font-bold text-[hsl(222,47%,11%)]">
                          {skill.level.toFixed(1)}
                        </p>
                        <p className="text-xs text-slate-500">{skill.skill.split(' ')[0]}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500 col-span-5">
                      Skill metrics will appear here after your first diagnostic analysis.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            </>
            )}
          </TabsContent>

          {/* Skills Tab */}
          <TabsContent value="skills" className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">Skill Metrics are updated as you complete exercises. Reach level 7+ to earn a Milestone badge.</p>
            <div className="grid gap-3" data-testid="skills-list">
              {loading ? (
                [1, 2, 3, 4].map((i) => (
                  <Card key={i} className="card-elevated border-0 animate-pulse">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-200/60 shrink-0"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <div className="h-4 w-32 bg-slate-200/60 rounded"></div>
                            <div className="h-4 w-12 bg-slate-200/60 rounded"></div>
                          </div>
                          <div className="h-2 w-full bg-slate-200/60 rounded"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : skillMetrics.length > 0 ? (
                skillMetrics.map((skill, idx) => (
                  <Card key={idx} className="card-elevated border-0">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center shrink-0">
                          <skill.icon className="w-6 h-6 text-[hsl(234,89%,63%)]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-[hsl(222,47%,11%)]">{skill.skill}</h3>
                            <div className="flex items-center gap-2">
                              <span className="text-xl font-bold text-[hsl(234,89%,63%)]">
                                {skill.level.toFixed(1)}/{skill.maxLevel}
                              </span>
                            </div>
                          </div>
                          <Progress
                            value={(skill.level / skill.maxLevel) * 100}
                            className="h-2"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-xs text-slate-500">
                  Complete your first diagnostic to see detailed skill metrics.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">Earn rewards at 7, 30, and 100-day streak milestones!</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="achievements-grid">
              {loading ? (
                [1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="card-elevated border-0 animate-pulse">
                    <CardContent className="p-5 text-center">
                      <div className="w-14 h-14 rounded-2xl mx-auto mb-3 bg-slate-200/60"></div>
                      <div className="h-4 w-24 mx-auto mb-3 bg-slate-200/60 rounded"></div>
                      <div className="h-3 w-32 mx-auto mb-4 bg-slate-200/60 rounded"></div>
                      <div className="h-5 w-20 mx-auto bg-slate-200/60 rounded-full"></div>
                    </CardContent>
                  </Card>
                ))
              ) : achievements.length > 0 ? achievements.map((achievement) => (
                <Card 
                  key={achievement.id} 
                  className={`card-elevated border-0 transition-all ${
                    achievement.earned ? '' : 'opacity-70'
                  }`}
                  data-testid={`achievement-${achievement.id}`}
                >
                  <CardContent className="p-5 text-center">
                    <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-gradient-to-br ${getRarityColor(achievement.rarity)} ${
                      achievement.earned ? 'shadow-md' : 'grayscale'
                    }`}>
                      <achievement.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-[hsl(222,47%,11%)] text-sm mb-1">{achievement.title}</h3>
                    <p className="text-xs text-slate-500 mb-3">{achievement.description}</p>
                    {achievement.earned ? (
                      <Badge className="bg-[hsl(142,76%,36%)]/10 text-[hsl(142,76%,36%)] text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {achievement.date}
                      </Badge>
                    ) : (
                      <div className="space-y-1.5">
                        <Progress value={achievement.progress} className="h-1" />
                        <span className="text-xs text-slate-500">{achievement.progress}%</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )) : (
                <p className="text-xs text-slate-500 col-span-12 lg:col-span-3">
                  You haven’t unlocked any milestones yet. As you progress, your achievements will appear here.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-4">
            <Card className="card-elevated border-0" data-testid="timeline-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Outfit']">Skill Improvement Timeline</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Your roadmap exercises and the skills they target</p>
              </CardHeader>
              <CardContent>
                {profileLoading ? (
                  <div className="relative">
                    <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-slate-200" />
                    <div className="space-y-5">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-start gap-4 relative animate-pulse">
                          <div className="relative z-10 bg-[hsl(220,20%,97%)] p-0.5">
                            <div className="w-4 h-4 rounded-full bg-slate-200" />
                          </div>
                          <div className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200/60 h-[100px]">
                            <div className="h-4 w-1/3 bg-slate-200 rounded mb-3"></div>
                            <div className="flex gap-2 mb-3">
                              <div className="h-3 w-16 bg-slate-200 rounded"></div>
                              <div className="h-3 w-16 bg-slate-200 rounded"></div>
                            </div>
                            <div className="h-1.5 w-full bg-slate-200 rounded"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : timelineItems.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    Your roadmap is not available yet. Complete onboarding to see your skill improvement timeline.
                  </p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-slate-200" />

                    <div className="space-y-5">
                      {timelineItems.map((item, idx) => (
                          <div key={item.id} className="flex items-start gap-4 relative" data-testid={`milestone-${idx}`}>
                            <div className="relative z-10 bg-[hsl(220,20%,97%)] p-0.5">
                              {getStatusIcon(item.status)}
                            </div>
                            <div
                              className={`flex-1 p-3 rounded-xl ${
                                item.status === 'completed'
                                  ? 'bg-[hsl(142,76%,36%)]/5 border border-[hsl(142,76%,36%)]/20'
                                  : item.status === 'current'
                                  ? 'bg-[hsl(234,89%,63%)]/5 border border-[hsl(234,89%,63%)]'
                                  : 'bg-slate-50 border border-slate-200/60'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <h4
                                  className={`font-medium text-sm ${
                                    item.status === 'upcoming' ? 'text-slate-400' : 'text-[hsl(222,47%,11%)]'
                                  }`}
                                >
                                  {item.title}
                                </h4>
                                {item.duration && (
                                  <span className="text-xs text-slate-500">{item.duration}</span>
                                )}
                              </div>
                              {item.targetSkills.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  <div className="flex flex-wrap gap-1.5">
                                    {item.targetSkills.map((sk) => {
                                      const Icon = skillIconMap[sk.key] || Target;
                                      return (
                                        <div
                                          key={sk.key}
                                          className="flex items-center gap-1.5 text-xs"
                                          title={`${sk.label}: ${sk.level}/${sk.maxLevel}`}
                                        >
                                          <Icon className="w-3.5 h-3.5 text-[hsl(234,89%,63%)]" />
                                          <span className="text-slate-600">{sk.label}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <Progress value={item.progress} className="h-1" />
                                </div>
                              )}
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
